// Sdílená logika pro seskupení laboratorních výsledků do oborů a jejich řazení.
//
// Řadí se primárně podle pole `order` (obor i parametr), sekundárně abecedně (česky).
// Dokud pořadí není v adminu vyplněné (u všech položek stejná hodnota / chybí),
// zůstává řazení podle abecedy díky tie-breaku.

const UNKNOWN_AREA_NAME = "Neznámá oblast";

// Komparátor: order (chybějící = na konec), pak český abecední tie-break podle názvu.
export const byOrderThenName = (getOrder, getName) => (a, b) => {
  const rawA = getOrder(a);
  const rawB = getOrder(b);
  const orderA = rawA === null || rawA === undefined ? Infinity : rawA;
  const orderB = rawB === null || rawB === undefined ? Infinity : rawB;
  if (orderA !== orderB) return orderA - orderB;
  return (getName(a) || "").localeCompare(getName(b) || "", "cs");
};

// Sestaví lookup mapu `id -> order` z pole (obory nebo definice parametrů).
export const buildOrderMap = (items = []) => {
  const map = {};
  items.forEach((item) => {
    if (item && item.id !== undefined && item.id !== null) {
      map[item.id] = item.order;
    }
  });
  return map;
};

// Sestaví lookup mapu `id -> objekt` (např. definice parametrů podle id).
export const buildByIdMap = (items = []) => {
  const map = {};
  items.forEach((item) => {
    if (item && item.id !== undefined && item.id !== null) {
      map[item.id] = item;
    }
  });
  return map;
};

const getOrderFromMap = (map, key) => {
  if (!map || key === undefined || key === null) return null;
  const value = map[key];
  return value === undefined ? null : value;
};

/**
 * Seskupí ploché pole ParameterResult do oborů.
 *
 * @param {Array} results - pole ParameterResult (z jednoho odběru nebo agregovaně).
 *   Každý result může mít navíc `made_at` a `sampling_id` (doplněné volajícím).
 * @param {Object} opts
 * @param {Object} opts.areaOrderById - mapa areaId -> order (z LaboratoryArea.order)
 * @param {Object} opts.paramOrderByDefId - mapa parameter_definition -> order
 * @returns {Array} [{ areaId, areaName, order, parameters: [
 *     { definitionId, key, name, abbreviation, units, order, results: [] }
 *   ] }]
 *   Obory i parametry jsou seřazené (obor -> parametr -> abeceda),
 *   výsledky uvnitř parametru sestupně podle `made_at`.
 */
export const groupResultsByArea = (
  results = [],
  { areaOrderById = {}, paramOrderByDefId = {} } = {}
) => {
  const areasMap = new Map();

  results.forEach((result) => {
    const areaId =
      result.area === undefined || result.area === null ? null : result.area;
    const areaKey = areaId === null ? "__none__" : areaId;

    if (!areasMap.has(areaKey)) {
      areasMap.set(areaKey, {
        areaId,
        areaName: result.area_name || UNKNOWN_AREA_NAME,
        order: getOrderFromMap(areaOrderById, areaId),
        parametersMap: new Map(),
      });
    }
    const area = areasMap.get(areaKey);

    const definitionId =
      result.parameter_definition === undefined ||
      result.parameter_definition === null
        ? null
        : result.parameter_definition;
    const paramKey =
      definitionId === null
        ? `${result.parameter_name}_${result.parameter_abbreviation}`
        : definitionId;

    if (!area.parametersMap.has(paramKey)) {
      area.parametersMap.set(paramKey, {
        definitionId,
        key: paramKey,
        name: result.parameter_name,
        abbreviation: result.parameter_abbreviation,
        units: result.parameter_units,
        order: getOrderFromMap(paramOrderByDefId, definitionId),
        results: [],
      });
    }
    area.parametersMap.get(paramKey).results.push(result);
  });

  const areas = Array.from(areasMap.values()).map((area) => {
    const parameters = Array.from(area.parametersMap.values());
    parameters.forEach((parameter) => {
      parameter.results.sort(
        (a, b) => new Date(b.made_at) - new Date(a.made_at)
      );
    });
    parameters.sort(
      byOrderThenName(
        (p) => p.order,
        (p) => p.name
      )
    );
    return {
      areaId: area.areaId,
      areaName: area.areaName,
      order: area.order,
      parameters,
    };
  });

  areas.sort(
    byOrderThenName(
      (a) => a.order,
      (a) => a.areaName
    )
  );

  return areas;
};
